import { useCallback, useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { Text } from '@/components/ui/text/Text';
import { SCREEN_PREFERENCE_CODES } from '@/routes';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { workingDayService } from '@/api/domains/workingDay';
import type { WorkingDayItem } from '@/api/domains/workingDay';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useWorkingDayEnableDisable } from '@/hooks/shift/useWorkingDayEnableDisable';


type WorkingDayFilters = Record<never, never>;

export interface UseWorkingDayListResult {
  rows: WorkingDayItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<WorkingDayItem>[];
  actions: TableAction<WorkingDayItem>[];
  onSearch: (term: string) => void;
  onEnterSearch: () => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  isLoading: boolean;
  openCreatePanel: () => void;
  refetch: () => void;
}

export function useWorkingDayList(): UseWorkingDayListResult {
  const { t: translate } = useTranslation();
  const { showApiError, openSidePanel } = useUI();
  const { canEdit, canCreate } = usePagePermissions();
  const { enableWorkingDay, disableWorkingDay, togglingIds } = useWorkingDayEnableDisable();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<WorkingDayFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<WorkingDayItem> | null> => {
      const response = await workingDayService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.key ? params.sort.direction.toUpperCase() : undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code !== 'ABORTED') {
          showApiError(response, translate('schedule:workingDay.messages.loadError'));
        }
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    flushSearch,
    refetch,
  } = usePaginatedList<WorkingDayItem, WorkingDayFilters>({
    fetchFn,
    initialFilters: {},
    orderByMap: {
      id: 'id',
      name: 'name',
      workingDays: 'workingDays',
      offDays: 'offDays',
      type: 'type',
      status: 'status',
    },
    screenCode: SCREEN_PREFERENCE_CODES.TURNOS_ROTATIVOS_FIJOS_WORKING_DAYS,
  });

  const openCreatePanel = useCallback(() => {
    if (!canCreate) return;
    openSidePanel({
      title: translate('schedule:workingDay.form.titleCreate'),
      component: 'WorkingDayFormPanel',
      props: { onSubmit: refetch },
    });
  }, [canCreate, openSidePanel, translate, refetch]);

  const handleEnable = useCallback(
    async (row: WorkingDayItem) => {
      const success = await enableWorkingDay(row.id);
      if (success) refetch();
    },
    [enableWorkingDay, refetch],
  );

  const handleDisable = useCallback(
    async (row: WorkingDayItem) => {
      const success = await disableWorkingDay(row.id);
      if (success) refetch();
    },
    [disableWorkingDay, refetch],
  );

  const openEditPanel = useCallback(
    (row: WorkingDayItem) => {
      openSidePanel({
        title: translate('schedule:workingDay.form.titleEdit'),
        component: 'WorkingDayFormPanel',
        props: {
          record: {
            id: row.id,
            name: row.name,
            workingDays: row.workingDays,
            offDays: row.offDays,
            workingDayTypeName: row.type,
          },
          onSubmit: refetch,
        },
      });
    },
    [openSidePanel, translate, refetch],
  );

  const columns: TableColumn<WorkingDayItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('schedule:workingDay.columns.id'),
        sortable: true,
        width: 80,
      },
      {
        key: 'name',
        header: translate('schedule:workingDay.columns.name'),
        sortable: true,
      },
      {
        key: 'workingDays',
        header: translate('schedule:workingDay.columns.workingDays'),
        sortable: true,
        render: (row: WorkingDayItem) => row.workingDays ?? '-',
      },
      {
        key: 'offDays',
        header: translate('schedule:workingDay.columns.offDays'),
        sortable: true,
        render: (row: WorkingDayItem) => row.offDays ?? '-',
      },
      {
        key: 'type',
        header: translate('schedule:workingDay.columns.type'),
        sortable: true,
        render: (row: WorkingDayItem) => row.type || '-',
      },
      {
        key: 'status',
        header: translate('schedule:workingDay.columns.status'),
        sortable: true,
        render: (row: WorkingDayItem) => {
          const statusLower = row.status?.toLowerCase();
          if (!statusLower) return '-';
          return (
            <Text
              variant="subheader-regular"
            >
              {statusLower === 'disabled'
                ? translate('schedule:workingDay.status.disabled')
                : translate('schedule:workingDay.status.enabled')}
            </Text>
          );
        },
      },
    ],
    [translate],
  );

  const actions: TableAction<WorkingDayItem>[] = useMemo(
    () => [
      {
        id: 'edit',
        label: translate('common:tableActions.edit'),
        icon: 'edit',
        hidden: () => !canEdit,
        onClick: openEditPanel,
      },
      {
        id: 'enable',
        label: translate('common:tableActions.enable'),
        icon: 'check',
        hidden: (row: WorkingDayItem) => !canEdit || row.status?.toLowerCase() !== 'disabled',
        onClick: (row: WorkingDayItem) => { void handleEnable(row); },
        disabled: (row: WorkingDayItem) => togglingIds.has(row.id),
        isLoading: (row: WorkingDayItem) => togglingIds.has(row.id),
      },
      {
        id: 'disable',
        label: translate('common:tableActions.disable'),
        icon: 'close',
        hidden: (row: WorkingDayItem) => !canEdit || row.status?.toLowerCase() !== 'enabled',
        onClick: (row: WorkingDayItem) => { void handleDisable(row); },
        disabled: (row: WorkingDayItem) => togglingIds.has(row.id),
        isLoading: (row: WorkingDayItem) => togglingIds.has(row.id),
        variant: 'danger',
      },
    ],
    [canEdit, translate, openEditPanel, handleEnable, handleDisable, togglingIds],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onEnterSearch: flushSearch,
    onSortChange,
    isLoading,
    openCreatePanel,
    refetch,
  };
}

